/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { chipSX, lastBreadcrumbSX } from '@/components/content/BreadcrumbTrail/styles/chip';
import { customBreadcrumbTrailContainerSX } from '@/components/content/BreadcrumbTrail/styles/container';
import { PAGE_TOKEN_NAME_CATEGORY, PAGE_TOKEN_NAME_PRODUCT } from '@/data/constants/layout';

import { usePlpEmarsysTracking } from '@/data/Content/_PlpEmarsysTracking';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLayout } from '@/data/Layout';
import { useSettings } from '@/data/Settings';
import { WidgetComponentProps } from '@/data/types/Slot';
import { mobileDisplaySx } from '@/styles/mobileApp';
import { getHref_Breadcrumb } from '@/utils/getHref_Breadcrumb';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import HomeIcon from '@mui/icons-material/Home';
import { Breadcrumbs, Chip, Link } from '@mui/material';
import { usePathname } from 'next/navigation';
import {
	buildCategoryEntityViewNameFromPath,
	useAdobeTargetEntityView,
} from 'presentation/events/hooks/adobe/useAdobeTargetEntityView';
import { FC } from 'react';

export const BreadcrumbTrail: FC<WidgetComponentProps> = ({ id }) => {
	const { breadcrumb, uniqueId } = useBreadcrumbTrail();
	const { uniqueId: physicalStoreId } = useDefaultStore();
	const { layout } = useLayout();
	const isProductPage = layout?.tokenName === PAGE_TOKEN_NAME_PRODUCT;
	const { isBuyable } = useProductDetails({
		partNumber: isProductPage ? id.toString() : '',
		physicalStoreIds: [physicalStoreId ?? ''],
	});
	const breadcrumbLength = breadcrumb?.length;
	const { settings } = useSettings();
	const isMobileApp = settings.isMobileApp;
	const pathname = usePathname();

	const isCategoryPage = layout?.tokenName === PAGE_TOKEN_NAME_CATEGORY;
	usePlpEmarsysTracking();

	const isDepartmentLandingPage = isCategoryPage && breadcrumbLength === 1;
	useAdobeTargetEntityView(
		isDepartmentLandingPage
			? buildCategoryEntityViewNameFromPath({
					pathname: pathname ?? undefined,
					categoryId: uniqueId,
			  })
			: undefined
	);

	if (!breadcrumbLength || isMobileApp || (!isCategoryPage && !isBuyable)) {
		return null;
	}

	return (
		<Breadcrumbs
			sx={{
				...customBreadcrumbTrailContainerSX.container(isCategoryPage),
				...mobileDisplaySx(isMobileApp),
			}}
			aria-label={breadcrumb?.at(-1)?.label}
			separator={<ArrowRightIcon fontSize="small" />}
		>
			<Link
				component={Linkable}
				key={`breadcrumb-${uniqueId}-home`}
				href="/"
				data-testid={`breadcrumb-${uniqueId}-home`}
				id={`breadcrumb-${uniqueId}-home`}
			>
				<Chip avatar={<HomeIcon />} label="Inicio" sx={chipSX} />
			</Link>
			{breadcrumb.map(({ href, label, trail }, index: number) => {
				const isLast = index === breadcrumbLength - 1;

				if (isLast) {
					return (
						<Chip
							key={`breadcrumb-${uniqueId}-${index}-${label}`}
							label={label}
							sx={lastBreadcrumbSX}
							data-testid={`breadcrumb-${uniqueId}-${index}-${label}`}
							id={`breadcrumb-${uniqueId}-${index}-${label}`}
						/>
					);
				}

				return (
					<Link
						component={Linkable}
						key={`breadcrumb-${uniqueId}-${index}-${label}`}
						href={getHref_Breadcrumb(href, trail)}
						sx={chipSX}
						data-testid={`breadcrumb-${uniqueId}-${index}-${label}`}
						id={`breadcrumb-${uniqueId}-${index}-${label}`}
					>
						{label}
					</Link>
				);
			})}
		</Breadcrumbs>
	);
};
